import { useCallback } from 'react'
import type { NerEntity, Tag, UpdateTag } from '@headline/schemas'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { tagsQuery, useCreateTag, useDeleteTag, useUpdateTag } from './queries'

/**
 * The sub-account's shared tag pool, backed by the real API. One pool reused across every topic in
 * the sub-account; topics reference tags by id. Previously localStorage; now a thin facade over the
 * tag query/mutations, keeping the same {tags, createTag, deleteTag} surface its consumers use.
 *
 * Deleting a tag is a server-side FK cascade (its topic/query mappings go with it), so the orphaned
 * -ids problem the wireframe called out no longer exists — but a delete is destructive, so the
 * resources page confirms it against the usage counts (tagUsageQuery) first.
 */
const EMPTY_TAGS: Tag[] = []

export function useResourceLibrary(subaccountId: string) {
  const queryClient = useQueryClient()
  const { data } = useQuery(tagsQuery(subaccountId))
  const tags = data?.tags ?? EMPTY_TAGS
  const createTagM = useCreateTag(subaccountId, queryClient)
  const updateTagM = useUpdateTag(subaccountId, queryClient)
  const deleteTagM = useDeleteTag(subaccountId, queryClient)

  const createTag = useCallback(
    (entity: NerEntity, value: string) => {
      const trimmed = value.trim()
      if (!trimmed) return
      createTagM.mutate({ entity, value: trimmed })
    },
    [createTagM],
  )

  /** Rename / re-type a tag in place — the id (and every topic/query mapping) stays. Callbacks let
   *  the edit dialog stay open on failure (a duplicate is a 409) and close only on success. */
  const updateTag = useCallback(
    (
      id: string,
      patch: UpdateTag,
      callbacks?: { onSuccess?: () => void; onError?: (message: string) => void },
    ) =>
      updateTagM.mutate(
        { tagId: id, ...patch },
        {
          ...(callbacks?.onSuccess && { onSuccess: callbacks.onSuccess }),
          ...(callbacks?.onError && {
            onError: (error: Error) => callbacks.onError?.(error.message),
          }),
        },
      ),
    [updateTagM],
  )

  const deleteTag = useCallback((id: string) => deleteTagM.mutate(id), [deleteTagM])

  return { tags, createTag, updateTag, deleteTag }
}
